import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Ban, Fingerprint, Boxes, PlugZap, PlusCircle, Siren, Radio, ShieldCheck, FileText, Camera, Loader2,
} from "lucide-react";
import { Panel, Btn, RiskBadge, Overline, Dot } from "../ui";
import { GaugeRing } from "../GaugeRing";
import { ThreatFeed } from "../ThreatFeed";
import { useI18n } from "../../lib/i18n";

const RISE_FROM = { opacity: 0, y: 14 };
const RISE_TO = { opacity: 1, y: 0 };
const SLIDE_FROM = { opacity: 0, x: -12 };
const SLIDE_TO = { opacity: 1, x: 0 };

const LOG_COLOR = {
  success: "text-aegis-emerald",
  blocked: "text-aegis-red",
  standard: "text-aegis-cyan",
  warning: "text-aegis-amber",
};

function Kpi({ label, value, unit, accent, icon: Icon, delay }) {
  const color = { red: "text-aegis-red", amber: "text-aegis-amber", emerald: "text-aegis-emerald", cyan: "text-aegis-cyan" }[accent];
  return (
    <motion.div
      initial={RISE_FROM}
      animate={RISE_TO}
      transition={{ delay }}
      className="rounded-xl border border-edge bg-panel/70 p-5"
    >
      <div className="flex items-start justify-between">
        <Overline>{label}</Overline>
        <Icon size={15} className={color} />
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className={`font-heading text-4xl font-light tabular ${color}`}>{value}</span>
        {unit && <span className="font-mono text-[10px] text-slate-400">{unit}</span>}
      </div>
    </motion.div>
  );
}

function OneClick({ icon: Icon, title, hint, tone, onClick, busy, testid }) {
  const tones = {
    emerald: "border-aegis-emerald/30 hover:border-aegis-emerald/60 text-aegis-emerald",
    cyan: "border-aegis-cyan/30 hover:border-aegis-cyan/60 text-aegis-cyan",
    amber: "border-aegis-amber/30 hover:border-aegis-amber/60 text-aegis-amber",
  };
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className={`group flex flex-col items-start gap-3 rounded-xl border raise p-5 text-left transition-colors duration-200 disabled:opacity-50 ${tones[tone]}`}
    >
      <span className="grid place-items-center h-11 w-11 rounded-lg border border-edge raise2">
        {busy ? <Loader2 size={20} className="animate-spin" /> : <Icon size={20} strokeWidth={1.7} />}
      </span>
      <span className="font-heading text-[15px] font-medium text-slate-100">{title}</span>
      <span className="text-[12px] leading-relaxed text-slate-400">{hint}</span>
    </button>
  );
}

export default function ExecutiveOS({
  status, summary, pending, logs, snapshot, onSign, onSimulate, onLockdown, onSealAll, onReport, onPreserve,
  onThreatIntercept, busy,
}) {
  const { t } = useI18n();
  const s = status || {};
  const sm = summary || {};
  const logRef = useRef(null);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  return (
    <div className="space-y-6" data-testid="screen-exec">
      <div>
        <Overline>{t("exec.overline")}</Overline>
        <h1 className="mt-1.5 font-heading text-3xl font-light tracking-tighter text-slate-100">{t("exec.title")}</h1>
        <p className="mt-2 text-sm text-slate-400">{t("exec.sub")}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="xl:col-span-5">
          <div className="rounded-xl border border-edge bg-panel/70 p-6">
            <div className="flex flex-wrap items-center justify-around gap-4">
              <GaugeRing
                value={sm.readiness_score ?? 0}
                label={t("exec.g.readiness")}
                sublabel="COURT READY"
                tone={(sm.readiness_score ?? 0) >= 70 ? "emerald" : (sm.readiness_score ?? 0) >= 40 ? "amber" : "red"}
                size={196}
                testid="gauge-readiness"
              />
              <div className="flex gap-4">
                <GaugeRing
                  value={sm.integrity_score ?? 0}
                  label={t("exec.g.integrity")}
                  tone={sm.chain_verified ? "cyan" : "red"}
                  size={118}
                  testid="gauge-integrity"
                />
                <GaugeRing
                  value={sm.custody_score ?? 0}
                  label={t("exec.g.custody")}
                  tone="purple"
                  size={118}
                  testid="gauge-custody"
                />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between border-t border-edge pt-4">
              <span className="flex items-center gap-2 text-[11px] text-slate-400">
                <ShieldCheck size={13} className={sm.chain_verified ? "text-aegis-emerald" : "text-aegis-red"} />
                {sm.evidence_sealed ?? 0}/{sm.evidence_total ?? 0} sealed · {sm.snapshots ?? 0} snapshots
              </span>
              <span className="font-mono text-[10px] text-slate-400">{sm.computed_at}</span>
            </div>
          </div>
        </div>

        <div className="xl:col-span-7 space-y-4">
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            <Kpi label={t("exec.kpi.blocked")} value={s.blocked_actions ?? "—"} unit="INTERCEPTED" accent="red" icon={Ban} delay={0.02} />
            <Kpi label={t("exec.kpi.pending")} value={s.pending_count ?? "—"} unit="FIDO2" accent="amber" icon={Fingerprint} delay={0.07} />
            <Kpi label={t("exec.kpi.height")} value={`#${s.block_height ?? "—"}`} unit="SHA-256" accent="emerald" icon={Boxes} delay={0.12} />
            <Kpi
              label={t("exec.kpi.sources")}
              value={`${sm.sources_connected ?? 0}/${sm.sources_total ?? 6}`}
              unit="READ-ONLY"
              accent="cyan"
              icon={PlugZap}
              delay={0.17}
            />
          </div>

          <Panel title={t("exec.one.title")} subtitle={t("exec.one.sub")} icon={Camera} accent="emerald" testid="one-click-console">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <OneClick
                icon={ShieldCheck}
                title={t("exec.btn.seal")}
                hint={t("exec.btn.seal.hint")}
                tone="emerald"
                onClick={onSealAll}
                busy={busy.seal}
                testid="oneclick-seal-btn"
              />
              <OneClick
                icon={FileText}
                title={t("exec.btn.report")}
                hint={t("exec.btn.report.hint")}
                tone="cyan"
                onClick={onReport}
                busy={busy.report}
                testid="oneclick-report-btn"
              />
              <OneClick
                icon={Camera}
                title={t("exec.btn.preserve")}
                hint={t("exec.btn.preserve.hint")}
                tone="amber"
                onClick={onPreserve}
                busy={busy.preserve}
                testid="oneclick-preserve-btn"
              />
            </div>
            {snapshot && (
              <div className="mt-4 rounded-lg border border-edge well px-4 py-3" data-testid="latest-snapshot">
                <Overline>{t("exec.snapshot.latest")}</Overline>
                <p className="mt-1 font-mono text-[11px] text-aegis-amber">
                  {snapshot.snapshot_id} · #{snapshot.block_height} · {snapshot.captured_at}
                </p>
                <p className="mt-1 break-all font-mono text-[10px] text-slate-400">{snapshot.snapshot_hash}</p>
              </div>
            )}
          </Panel>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="xl:col-span-12">
          <ThreatFeed onIntercept={onThreatIntercept} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="xl:col-span-8">
          <Panel
            title={t("exec.pending.title")}
            subtitle={t("exec.pending.sub")}
            icon={Fingerprint}
            accent="amber"
            testid="approval-gate-panel"
            actions={
              <div className="flex flex-wrap gap-2">
                <Btn variant="ghost" onClick={onSimulate} testid="simulate-action-btn">
                  <PlusCircle size={15} /> {t("exec.simulate")}
                </Btn>
                <Btn variant="redGhost" onClick={onLockdown} disabled={s.lockdown_active} testid="trigger-lockdown-btn">
                  <Siren size={15} /> {t("exec.lockdown")}
                </Btn>
              </div>
            }
          >
            <div className="space-y-3">
              {(!pending || pending.length === 0) && (
                <div className="rounded-lg border border-dashed border-edge py-10 text-center text-sm text-slate-400">
                  {t("exec.pending.empty")}
                </div>
              )}
              {pending?.map((a, i) => (
                <motion.div
                  key={a.req_id}
                  initial={SLIDE_FROM}
                  animate={SLIDE_TO}
                  transition={{ delay: i * 0.05 }}
                  className="relative rounded-lg border border-edge raise p-4 pl-5"
                  data-testid={`pending-action-${a.req_id}`}
                >
                  <span
                    className={`absolute left-0 top-3 bottom-3 w-0.5 rounded-full ${
                      a.risk_level === "HIGH_RISK" ? "bg-aegis-red" : "bg-aegis-amber"
                    }`}
                  />
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <RiskBadge level={a.risk_level} />
                        <span className="font-mono text-sm text-slate-100">{a.req_id}</span>
                        <span className="font-mono text-xs text-slate-400">| {a.action_type}</span>
                      </div>
                      <p className="mt-2 text-[13px] text-slate-300">{a.scope}</p>
                      <p className="mt-1 text-xs text-slate-400">{a.reason}</p>
                      <p className="mt-2 font-mono text-[11px] text-slate-400">
                        {a.digest} · {a.detected_at}
                      </p>
                    </div>
                    <Btn variant="amber" onClick={() => onSign(a)} testid={`sign-btn-${a.req_id}`} className="shrink-0">
                      <Fingerprint size={15} /> {t("exec.sign")}
                    </Btn>
                  </div>
                </motion.div>
              ))}
            </div>
          </Panel>
        </div>

        <div className="xl:col-span-4">
          <Panel title={t("exec.heartbeat")} subtitle="Live console stream" icon={Radio} accent="cyan" testid="heartbeat-panel">
            <div className="mb-2 flex items-center gap-2 text-[11px] text-slate-400">
              <Dot color="emerald" /> streaming · read-only plane
            </div>
            <div
              ref={logRef}
              className="h-[380px] overflow-y-auto rounded-lg border border-edge well p-3 font-mono text-[11px] leading-relaxed no-scrollbar"
            >
              {logs?.map((l, i) => (
                <div key={i} className="flex gap-2 py-0.5">
                  <span className="shrink-0 text-slate-400">
                    {new Date(l.timestamp).toLocaleTimeString("en-GB", { hour12: false })}
                  </span>
                  <span className={LOG_COLOR[l.level] || "text-slate-300"}>{l.message}</span>
                </div>
              ))}
              <span className="animate-pulseGlow text-slate-400">▍</span>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
